import { useCallback, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { AnalyticsEvent, analyticsProxyApi } from '../../services';
import { CookieActionType, cookieOptions, getOrganisationDomain } from '../utils';

enum AnalyticsEventName {
  item_shared = 'item_shared',
  item_played = 'item_played',
  click_open_article = 'click_open_article',
  click_open_mix = 'click_open_mix',
  push_open = 'push_open',
  session_start = 'session_start',
  session_end = 'session_end',
  iam = 'iam',
  post_video_played = 'post_video_played',
  post_audio_played = 'post_audio_played',
  post_click_open_link = 'post_click_open_link',
  event_shared = 'event_shared',
}

interface ItemSharedPayload {
  user_Id: string;
  channel_id: number;
  story_id: number;
  item_id: number;
  time_stamp: string;
}

interface ItemPlayedPayload {
  user_Id: string;
  channel_id: number;
  story_id: number;
  item_id: number;
  duration: number;
  time_stamp: string;
}

interface ClickOpenArticlePayload {
  user_Id: string;
  channel_id: number;
  story_id: number;
  item_id: number;
  time_stamp: string;
}

interface ClickOpenStoryPayload {
  channel_id: number;
  story_id: number;
  mix_id: number;
  duration: number;
  time_stamp: string;
}

interface PushOpenPayload {
  channel_id: number;
  notification_id: number;
  time_stamp: string;
}

interface SessionStartPayload {
  time_stamp: string;
}

interface SessionEndPayload {
  channel_id: number;
  duration: number;
  time_stamp: string;
}

interface InAppMessageViewPayload {
  iam_id: number;
  iam_action: 'primary' | 'secondary';
  time_stamp: string;
}

interface PostVideoPlayedPayload {
  channel_id: number;
  block_id: string;
  story_id: number;
  item_id: number;
  duration: number;
  time_stamp: string;
}

interface PostAudioPlayedPayload {
  channel_id: number;
  block_id: string;
  story_id: number;
  item_id: number;
  duration: number;
  time_stamp: string;
}

interface EventSharedPayload {
  user_Id: string;
  channel_id: number;
  event_id: number;
  time_stamp: string;
}

interface PostOpenLinkPayload {
  channel_id: number;
  block_id: string;
  story_id: number;
  item_id: number;
  time_stamp: string;
}

export const useAnalytics = () => {
  const [logEvent] = analyticsProxyApi.endpoints.logEvent.useMutation();

  const [cookies, , removeCookie] = useCookies();

  const organisationDomain = useMemo(() => getOrganisationDomain(), []);

  const clientId = useMemo(() => localStorage.getItem('clientId'), []);

  const logEventHandler = useCallback(
    (event: AnalyticsEvent) => {
      if (!clientId) {
        return;
      }

      logEvent({ event, organisationDomain, clientId });

      console.log(event.name);
    },
    [clientId, logEvent, organisationDomain]
  );

  useEffect(() => {
    if (!clientId) {
      localStorage.setItem('clientId', `${Date.now()}.${Math.floor(Math.random() * 1000000000)}`);
    }

    Object.keys(cookies).forEach((cookieName) => {
      if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid')) {
        removeCookie(cookieName, cookieOptions({ action: CookieActionType.REMOVE }));
      }
    });
  }, [clientId, cookies, organisationDomain, removeCookie]);

  const logItemShared = useCallback(
    (payload: ItemSharedPayload) =>
      logEventHandler({ name: AnalyticsEventName.item_shared, params: payload }),
    [logEventHandler]
  );

  const logItemPlayed = useCallback(
    (payload: ItemPlayedPayload) => {
      if (!payload.duration) {
        return;
      }

      logEventHandler({ name: AnalyticsEventName.item_played, params: payload });

      console.log(payload.duration + ' seconds');
    },
    [logEventHandler]
  );

  const logPostVideoPlayed = useCallback(
    (payload: PostVideoPlayedPayload) => {
      if (!payload.duration) {
        return;
      }

      logEventHandler({ name: AnalyticsEventName.post_video_played, params: payload });

      console.log(payload.duration + ' seconds');
    },
    [logEventHandler]
  );

  const logPostAudioPlayed = useCallback(
    (payload: PostAudioPlayedPayload) => {
      if (!payload.duration) {
        return;
      }

      logEventHandler({ name: AnalyticsEventName.post_audio_played, params: payload });

      console.log(payload.duration + ' seconds');
    },
    [logEventHandler]
  );

  const logEventShared = useCallback(
    (payload: EventSharedPayload) =>
      logEventHandler({ name: AnalyticsEventName.event_shared, params: payload }),
    [logEventHandler]
  );

  const logPostOpenLink = useCallback(
    (payload: PostOpenLinkPayload) =>
      logEventHandler({ name: AnalyticsEventName.post_click_open_link, params: payload }),
    [logEventHandler]
  );

  const logClickOpenArticle = useCallback(
    (payload: ClickOpenArticlePayload) =>
      logEventHandler({ name: AnalyticsEventName.click_open_article, params: payload }),
    [logEventHandler]
  );

  const logClickOpenStory = useCallback(
    (payload: ClickOpenStoryPayload) => {
      if (!payload.duration) {
        return;
      }

      logEventHandler({ name: AnalyticsEventName.click_open_mix, params: payload });

      console.log(payload.duration + ' seconds');
    },
    [logEventHandler]
  );

  const logPushOpen = useCallback(
    (payload: PushOpenPayload) =>
      logEventHandler({ name: AnalyticsEventName.push_open, params: payload }),
    [logEventHandler]
  );

  const logSessionStart = useCallback(
    (payload: SessionStartPayload) =>
      logEventHandler({ name: AnalyticsEventName.session_start, params: payload }),
    [logEventHandler]
  );

  const logSessionEnd = useCallback(
    (payload: SessionEndPayload) => {
      if (!payload.duration) {
        return;
      }

      logEventHandler({ name: AnalyticsEventName.session_end, params: payload });

      console.log(payload.duration + ' seconds');
    },
    [logEventHandler]
  );

  const logInAppMessageView = useCallback(
    (payload: InAppMessageViewPayload) =>
      logEventHandler({ name: AnalyticsEventName.iam, params: payload }),
    [logEventHandler]
  );

  return {
    logEventShared,
    logItemShared,
    logItemPlayed,
    logPostVideoPlayed,
    logPostAudioPlayed,
    logPostOpenLink,
    logClickOpenArticle,
    logClickOpenStory,
    logPushOpen,
    logSessionStart,
    logSessionEnd,
    logInAppMessageView,
  };
};
